import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Plus, Search, Phone, Mail, DollarSign, X } from 'lucide-react';

const STAGES = [
  { key: 'NEW_LEAD', label: 'New Lead', color: 'bg-blue-500' },
  { key: 'CONTACTED', label: 'Contacted', color: 'bg-sky-500' },
  { key: 'CONSULT_BOOKED', label: 'Consult Booked', color: 'bg-violet-500' },
  { key: 'NO_SHOW', label: 'No Show', color: 'bg-red-500' },
  { key: 'TREATMENT_PRESENTED', label: 'Treatment Presented', color: 'bg-amber-500' },
  { key: 'UNSOLD_TREATMENT', label: 'Unsold Treatment', color: 'bg-orange-500' },
  { key: 'WON', label: 'Won', color: 'bg-emerald-500' },
  { key: 'LOST', label: 'Lost', color: 'bg-slate-400' },
  { key: 'REACTIVATED', label: 'Reactivated', color: 'bg-teal-500' },
];

export default function Leads() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [loading, setLoading] = useState(true);
  const [newLead, setNewLead] = useState({ first_name: '', last_name: '', phone: '', email: '', treatment_interest: '', estimated_value: '' });

  const fetchLeads = useCallback(async () => {
    try {
      const { data } = await api().get('/api/leads', { params: { limit: 500, search: search || undefined } });
      setLeads(data.leads || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api, search]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const getLeadsByStage = (stage) => leads.filter(l => l.pipeline_stage === stage);

  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    const { draggableId, destination } = result;
    const newStage = destination.droppableId;

    setLeads(prev => prev.map(l => l.id === draggableId ? { ...l, pipeline_stage: newStage } : l));

    try {
      await api().patch(`/api/leads/${draggableId}/pipeline`, { pipeline_stage: newStage });
    } catch {
      fetchLeads();
    }
  };

  const handleAddLead = async (e) => {
    e.preventDefault();
    try {
      await api().post('/api/leads', {
        ...newLead,
        estimated_value: newLead.estimated_value ? parseFloat(newLead.estimated_value) : null,
      });
      setNewLead({ first_name: '', last_name: '', phone: '', email: '', treatment_interest: '', estimated_value: '' });
      setShowAdd(false);
      fetchLeads();
    } catch (e) { console.error(e); }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div data-testid="leads-page" className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Lead Pipeline</h1>
          <p className="text-sm text-slate-500 mt-0.5">{leads.length} total leads</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="leads-search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 w-48"
              placeholder="Search leads..."
            />
          </div>
          <button
            data-testid="leads-add-btn"
            onClick={() => setShowAdd(true)}
            className="flex items-center gap-2 bg-slate-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-slate-800 transition-all"
          >
            <Plus size={16} /> Add Lead
          </button>
        </div>
      </div>

      {/* Kanban Board */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="flex-1 overflow-x-auto pb-4">
          <div className="flex gap-4" style={{ minWidth: `${STAGES.length * 300}px` }}>
            {STAGES.map(stage => (
              <Droppable key={stage.key} droppableId={stage.key}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    data-testid={`pipeline-column-${stage.key}`}
                    className={`w-[280px] shrink-0 rounded-xl border transition-colors ${
                      snapshot.isDraggingOver ? 'bg-slate-100 border-slate-300' : 'bg-slate-50 border-slate-200'
                    }`}
                  >
                    <div className="p-3 border-b border-slate-200">
                      <div className="flex items-center gap-2">
                        <div className={`w-2.5 h-2.5 rounded-full ${stage.color}`} />
                        <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                          {stage.label}
                        </span>
                        <span className="ml-auto text-xs text-slate-400 font-medium">
                          {getLeadsByStage(stage.key).length}
                        </span>
                      </div>
                    </div>
                    <div className="p-2 space-y-2 min-h-[120px] max-h-[calc(100vh-280px)] overflow-y-auto">
                      {getLeadsByStage(stage.key).map((lead, index) => (
                        <Draggable key={lead.id} draggableId={lead.id} index={index}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              data-testid={`lead-card-${lead.id}`}
                              onClick={() => navigate(`/leads/${lead.id}`)}
                              className={`bg-white rounded-lg border border-slate-200 p-3 cursor-pointer hover:shadow-sm transition-all ${
                                snapshot.isDragging ? 'shadow-lg ring-2 ring-slate-900/10' : ''
                              }`}
                            >
                              <p className="text-sm font-medium text-slate-900 truncate">
                                {lead.first_name} {lead.last_name}
                              </p>
                              <div className="flex items-center gap-3 mt-2">
                                {lead.phone && (
                                  <span className="flex items-center gap-1 text-xs text-slate-400">
                                    <Phone size={10} /> {lead.phone}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center justify-between mt-2">
                                {lead.treatment_interest && (
                                  <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full truncate max-w-[140px]">
                                    {lead.treatment_interest}
                                  </span>
                                )}
                                {lead.estimated_value && (
                                  <span className="flex items-center gap-0.5 text-xs font-medium text-emerald-600">
                                    <DollarSign size={10} />{lead.estimated_value.toLocaleString()}
                                  </span>
                                )}
                              </div>
                              {lead.source === 'IMPORT' && (
                                <span className="mt-2 inline-block text-[10px] font-medium bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded">
                                  Imported
                                </span>
                              )}
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  </div>
                )}
              </Droppable>
            ))}
          </div>
        </div>
      </DragDropContext>

      {/* Add Lead Modal */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowAdd(false)}>
          <div className="bg-white rounded-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-slate-900">Add New Lead</h2>
              <button onClick={() => setShowAdd(false)} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
            </div>
            <form onSubmit={handleAddLead} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">First Name *</label>
                  <input data-testid="add-lead-first-name" required value={newLead.first_name}
                    onChange={e => setNewLead(p => ({ ...p, first_name: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Last Name *</label>
                  <input data-testid="add-lead-last-name" required value={newLead.last_name}
                    onChange={e => setNewLead(p => ({ ...p, last_name: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Phone *</label>
                <input data-testid="add-lead-phone" required value={newLead.phone}
                  onChange={e => setNewLead(p => ({ ...p, phone: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900"
                  placeholder="+1 555 123 4567" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Email</label>
                <input data-testid="add-lead-email" type="email" value={newLead.email}
                  onChange={e => setNewLead(p => ({ ...p, email: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Treatment Interest</label>
                <input data-testid="add-lead-treatment" value={newLead.treatment_interest}
                  onChange={e => setNewLead(p => ({ ...p, treatment_interest: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900"
                  placeholder="Veneers, Implants, etc." />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Estimated Value ($)</label>
                <input data-testid="add-lead-value" type="number" value={newLead.estimated_value}
                  onChange={e => setNewLead(p => ({ ...p, estimated_value: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900"
                  placeholder="5000" />
              </div>
              <button data-testid="add-lead-submit" type="submit"
                className="w-full bg-slate-900 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-slate-800 transition-all">
                Add Lead
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
